class ScienceVideoScroll extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.sticky = this.querySelector('.science-scroll__sticky');
    this.frameCount = parseInt(this.dataset.frameCount, 10);
    this.frames = new Array(this.frameCount).fill(null);
    this.currentFrame = 0;
    this.ticking = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.prevProgress = 0;
    this.jumped = false;

    this.buildUrls();

    this.resize = this.resize.bind(this);
    this.onScroll = this.onScroll.bind(this);

    window.addEventListener('resize', this.resize, { passive: true });
    if (!this.reducedMotion) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    requestAnimationFrame(() => this.applyMobileHeaderOffset());
    this.resize();
    this.preload();
  }

  applyMobileHeaderOffset() {
    if (window.matchMedia('(min-width: 768px)').matches) return;
    if (!this.sticky) return;
    const header = document.querySelector('.header-section');
    const h = header ? Math.round(header.getBoundingClientRect().height) : 0;
    if (!h) return;
    this.sticky.style.top = h + 'px';
    this.sticky.style.height = `calc(100svh - ${h}px)`;
    this.sticky.style.paddingTop = '2rem';
    document.documentElement.style.setProperty('scroll-padding-top', h + 'px');
  }

  buildUrls() {
    const firstFrame = this.dataset.firstFrame;
    if (!firstFrame) { this.frameUrls = []; return; }
    const base = firstFrame.split('?')[0].replace(/[^/]+$/, '');
    const prefix = this.dataset.framePrefix;
    const digits = parseInt(this.dataset.frameDigits, 10);
    const start = parseInt(this.dataset.frameStart || '0', 10);
    const ext = this.dataset.frameExt || 'png';
    this.frameUrls = Array.from({ length: this.frameCount }, (_, i) =>
      `${base}${prefix}${String(start + i).padStart(digits, '0')}.${ext}`
    );
  }

  preload() {
    this.loadImage(0, () => this.draw(0));
    for (let i = 1; i < this.frameCount; i++) {
      this.loadImage(i);
    }
  }

  loadImage(index, callback) {
    const img = new Image();
    img.onload = () => {
      this.frames[index] = img;
      if (callback) callback();
    };
    img.src = this.frameUrls[index];
  }

  resize() {
    this.applyMobileHeaderOffset();
    if (this.frames[this.currentFrame]) this.draw(this.currentFrame);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  update() {
    const scrollY = window.scrollY;
    const top = scrollY + this.getBoundingClientRect().top;
    const scrollable = this.offsetHeight - window.innerHeight;
    const rawProgress = (scrollY - top) / scrollable;
    const progress = Math.max(0, Math.min(1, rawProgress));
    const index = Math.round(progress * (this.frameCount - 1));

    if (index !== this.currentFrame) {
      this.currentFrame = index;
      for (let i = index; i >= 0; i--) {
        if (this.frames[i]) {
          this.draw(i);
          break;
        }
      }
    }

    // When animation completes while scrolling down, jump past the exit phase
    if (rawProgress >= 1 && this.prevProgress < 1 && !this.jumped) {
      this.jumped = true;
      const sectionBottom = top + this.offsetHeight;
      window.scrollTo({ top: sectionBottom - window.innerHeight, behavior: 'smooth' });
    }

    // Reset jump flag when scrolling back up into the section
    if (rawProgress < 0.95) {
      this.jumped = false;
    }

    this.prevProgress = rawProgress;
  }

  draw(index) {
    const img = this.frames[index];
    if (!img) return;
    // Buffer is fixed at 2:1 (matching CSS aspect-ratio) so layout is never
    // affected regardless of the image's native dimensions.
    // Images that aren't 2:1 are contain-scaled with transparent side margins.
    const bufW = img.naturalWidth;
    const bufH = img.naturalWidth >> 1;
    if (this.canvas.width !== bufW || this.canvas.height !== bufH) {
      this.canvas.width = bufW;
      this.canvas.height = bufH;
    }
    const scale = Math.min(bufW / img.naturalWidth, bufH / img.naturalHeight);
    const dx = (bufW - img.naturalWidth * scale) / 2;
    const dy = (bufH - img.naturalHeight * scale) / 2;
    this.ctx.clearRect(0, 0, bufW, bufH);
    this.ctx.drawImage(img, dx, dy, img.naturalWidth * scale, img.naturalHeight * scale);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.resize);
  }
}

customElements.define('science-video-scroll', ScienceVideoScroll);
